import React, { useMemo } from "react";
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Stack,
  Theme,
  Tooltip,
  useTheme,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { ContentCopy } from "@mui/icons-material";
import { Coupon } from "../../../../../models/inventory/Coupon";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { SortType } from "../../../../../models/SortType";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import {
  getModernPageColors,
  modernEmptyStateSx,
} from "../../../../../styles/modernListingPageSx";
import { useNotification } from "../../../../../utils/NotificationsContext";

interface CouponsTableProp {
  maxHeight: number;
  coupons: Coupon[];
  allSelected: boolean;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  selectedShipmentIdsMap: Map<number, number>;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  selectAllItem: () => void;
  selectItem: (coupon: Coupon) => void;
  deSelectAllItem: () => void;
  deSelectItem: (coupon: Coupon) => void;
  onViewDetails: (coupon: Coupon) => void;
}

const hasExpiryDatePassed = (expiry_date?: Date | string | null) => {
  if (!expiry_date) return false;
  const expiry = new Date(expiry_date);
  if (Number.isNaN(expiry.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);
  return expiry.getTime() <= today.getTime();
};

const getCouponStatus = (coupon: Coupon) => {
  if (coupon.redeemed) return "Redeemed";
  if (coupon.expired) return "Expired";
  if (coupon.reactivated) return "Reactivated";
  if (coupon.opened) return "Opened";
  if (coupon.sent) return "Sent";
  return "Not Sent";
};

const getCouponStatusTones = (
  theme: Theme,
): Record<string, { color: string; chipBg: string; cardBg: string }> => {
  const c = theme.palette.customColors;
  return {
    "Not Sent": {
      color: theme.palette.orange.dark,
      chipBg: c.lightOrange,
      cardBg: theme.palette.brown.main,
    },
    Sent: {
      color: c.purple,
      chipBg: c.lightPurple,
      cardBg: c.lightBlue,
    },
    Opened: {
      color: c.darkBlue,
      chipBg: c.lightBlue,
      cardBg: c.blueLightBackground,
    },
    Redeemed: {
      color: c.successGreen,
      chipBg: c.successGreenLightBorder,
      cardBg: c.successGreenLightBackground,
    },
    Expired: {
      color: c.errorRed,
      chipBg: theme.palette.blushPink.main,
      cardBg: theme.palette.blushPink.light,
    },
    Reactivated: {
      color: c.tealGradientStart,
      chipBg: c.greenLight,
      cardBg: c.lightGreenBackground,
    },
  };
};

const CouponsTable = ({
  maxHeight,
  coupons,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  selectedShipmentIdsMap,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  selectAllItem,
  selectItem,
  deSelectAllItem,
  deSelectItem,
  onViewDetails,
}: CouponsTableProp) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const statusTones = getCouponStatusTones(theme);
  const { showError } = useNotification();

  const sortOptions = useMemo(
    () => [
      { key: "id", label: "Coupon ID" },
      { key: "created_date", label: "Created" },
      { key: "sent_date", label: "Sent" },
      { key: "expiry_date", label: "Expiry" },
      { key: "modified_date", label: "Modified" },
    ],
    [],
  );

  const areSomeSelected = () =>
    selectedShipmentIdsMap.size > 0 &&
    selectedShipmentIdsMap.size !== coupons.length;

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const handleSortClick = (column: string) => {
    const nextSortType =
      sortBy === column && sortType === SortType.DESC
        ? SortType.ASC
        : SortType.DESC;
    onSortChange(column, nextSortType);
  };

  const actionButtonStyles = {
    minWidth: "fit-content",
    minHeight: 30,
    px: 1.5,
    fontSize: "0.72rem",
    fontWeight: 700,
    color: colors.accent,
    backgroundColor: colors.surfaceSoft,
    border: `1px solid ${colors.borderSoft}`,
    boxShadow: "none",
    transition: "all 0.2s ease",
    "&:hover": {
      color: colors.surface,
      backgroundColor: colors.accent,
      borderColor: colors.accent,
      boxShadow: colors.shadowSoft,
    },
  };

  const handleCopyText = async (
    event: React.MouseEvent,
    value?: string | number | null,
    label = "Value",
  ) => {
    event.stopPropagation();
    const text =
      value === undefined || value === null ? "" : String(value).trim();

    if (!text || text === "-") {
      showError("Copy failed", `Nothing to copy for ${label.toLowerCase()}`);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error(`Failed to copy ${label}`, error);
    }
  };

  const renderCopyButton = (
    value?: string | number | null,
    label = "Value",
  ) => (
    <Tooltip title={`Copy ${label}`}>
      <IconButton
        size="small"
        onClick={(event) => handleCopyText(event, value, label)}
        sx={{
          p: 0.25,
          color: colors.subtitle,
          "&:hover": {
            backgroundColor: colors.surfaceMuted,
            color: theme.palette.primary.main,
          },
        }}
      >
        <ContentCopy sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );

  const getRecipientName = (coupon: Coupon) => {
    const firstName = coupon.user_first_name || coupon.first_name || "";
    const lastName = coupon.user_last_name || coupon.last_name || "";
    return `${firstName} ${lastName}`.trim() || "-";
  };

  const getRecipientEmail = (coupon: Coupon) =>
    coupon.user_email || coupon.email || "-";

  const truncateText = (value?: string | null, max = 48) => {
    if (!value) return "-";
    return value.length > max ? `${value.slice(0, max)}...` : value;
  };

  const getCardBackgroundColor = (coupon: Coupon) => {
    const status = getCouponStatus(coupon);
    return statusTones[status]?.cardBg || theme.palette.customColors.white;
  };

  const getStatusChipSx = (coupon: Coupon) => {
    const status = getCouponStatus(coupon);
    const tone = statusTones[status] || statusTones["Not Sent"];
    return {
      height: 22,
      fontSize: "0.68rem",
      fontWeight: 700,
      backgroundColor: tone.chipBg,
      color: tone.color,
      border: `1px solid ${tone.color}33`,
    };
  };

  const renderMeta = (label: string, value?: React.ReactNode) => (
    <Box sx={{ minWidth: 0 }}>
      <CustomTypography
        variant="body3"
        color={colors.subtitle}
        fontWeight={600}
        sx={{ display: "block" }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="body2"
        color={colors.title}
        fontWeight={500}
        sx={{
          fontSize: "0.78rem",
          lineHeight: 1.35,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          wordBreak: "break-word",
        }}
      >
        {value || "-"}
      </CustomTypography>
    </Box>
  );

  return (
    <Paper
      sx={{
        marginTop: "0.6rem",
        borderRadius: "16px",
        border: `1px solid ${colors.border}`,
        boxShadow: colors.shadowSoft,
        overflow: "hidden",
        backgroundColor: colors.surface,
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor: colors.surfaceMuted,
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Checkbox
              color="primary"
              indeterminate={areSomeSelected()}
              checked={
                coupons.length > 0 &&
                selectedShipmentIdsMap.size === coupons.length
              }
              onClick={() => {
                coupons.length > 0 &&
                selectedShipmentIdsMap.size === coupons.length
                  ? deSelectAllItem()
                  : selectAllItem();
              }}
            />
            <Box>
              <CustomTypography variant="heading7" fontWeight={700}>
                Coupons
              </CustomTypography>
              <CustomTypography variant="caption" color={colors.subtitle}>
                {selectedShipmentIdsMap.size > 0
                  ? `${selectedShipmentIdsMap.size} selected on this page`
                  : `${total_count} total coupons`}
              </CustomTypography>
            </Box>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
          >
            <CustomTypography variant="caption" color={colors.subtitle}>
              Sort by
            </CustomTypography>
            {sortOptions.map((option) => {
              const isActive = sortBy === option.key;
              const directionLabel =
                isActive && sortType === SortType.ASC ? "Asc" : "Desc";

              return (
                <AppButton
                  key={option.key}
                  label={
                    isActive
                      ? `${option.label}: ${directionLabel}`
                      : option.label
                  }
                  onClick={() => handleSortClick(option.key)}
                  customStyles={{
                    minWidth: "fit-content",
                    height: 34,
                    px: 1.5,
                    borderRadius: "999px",
                    color: isActive ? colors.surface : colors.title,
                    backgroundColor: isActive
                      ? theme.palette.primary.main
                      : colors.surface,
                    border: `1px solid ${
                      isActive ? theme.palette.primary.main : colors.border
                    }`,
                    "&:hover": {
                      backgroundColor: isActive
                        ? theme.palette.primary.dark
                        : colors.surfaceMuted,
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          maxHeight,
          overflowY: "auto",
          p: { xs: 1, sm: 1.5 },
          backgroundColor: colors.pageBg,
        }}
      >
        <Stack spacing={1}>
          {coupons.map((coupon) => {
            const isSelected = selectedShipmentIdsMap.has(coupon.id);
            const rowColor = getCardBackgroundColor(coupon);
            const email = getRecipientEmail(coupon);

            return (
              <Paper
                key={coupon.id}
                onClick={() => onViewDetails(coupon)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: rowColor,
                  border: isSelected
                    ? `1px solid ${theme.palette.primary.main}`
                    : `1px solid ${colors.border}`,
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <Box sx={{ px: { xs: 1, sm: 1.25 }, pt: 1, pb: 0.75 }}>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="flex-start"
                  >
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={isSelected}
                      onClick={(event) => {
                        event.stopPropagation();
                        isSelected
                          ? deSelectItem(coupon)
                          : selectItem(coupon);
                      }}
                      sx={{ p: 0.25 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                      >
                        <Stack
                          direction="row"
                          spacing={0.75}
                          useFlexGap
                          flexWrap="wrap"
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.25}
                            alignItems="center"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <CustomTypography
                              variant="heading7"
                              fontWeight={700}
                              color={colors.title}
                              sx={{ fontSize: "0.9rem", lineHeight: 1.2 }}
                            >
                              #{coupon.id}
                            </CustomTypography>
                            {renderCopyButton(coupon.id, "Coupon ID")}
                          </Stack>
                          {coupon.code ? (
                            <Stack
                              direction="row"
                              spacing={0.25}
                              alignItems="center"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <CustomTypography
                                variant="body2"
                                color={colors.subtitle}
                                fontWeight={600}
                                sx={{ lineHeight: 1.2 }}
                              >
                                {coupon.code}
                              </CustomTypography>
                              {renderCopyButton(coupon.code, "Coupon code")}
                            </Stack>
                          ) : null}
                        </Stack>

                        <CustomTypography
                          variant="body3"
                          color={
                            hasExpiryDatePassed(coupon.expiry_date) &&
                            !coupon.redeemed
                              ? colors.error
                              : colors.subtitle
                          }
                          fontWeight={600}
                          sx={{ display: "block", flexShrink: 0 }}
                        >
                          Expiry {formatDate(coupon.expiry_date)}
                        </CustomTypography>
                      </Stack>

                      <Grid2
                        container
                        spacing={1}
                        sx={{ mt: 1.5, mb: 1.5, alignItems: "center" }}
                      >
                        <Grid2 size={{ xs: 6, sm: 3, md: 2 }}>
                          <Chip
                            label={getCouponStatus(coupon).toUpperCase()}
                            size="small"
                            sx={getStatusChipSx(coupon)}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 3, md: 2 }}>
                          <Chip
                            label={coupon.active ? "ACTIVE" : "INACTIVE"}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.68rem",
                              backgroundColor: colors.surface,
                              border: `1px solid ${colors.border}`,
                            }}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 3, md: 2 }}>
                          <CustomTypography
                            variant="body2"
                            color={colors.subtitle}
                            fontWeight={600}
                          >
                            Order ID #{coupon.order_id || "-"}
                          </CustomTypography>
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 3, md: 2 }}>
                          <CustomTypography
                            variant="body2"
                            color={colors.subtitle}
                            fontWeight={600}
                          >
                            Inventory ID #{coupon.inventory_id || "-"}
                          </CustomTypography>
                        </Grid2>
                      </Grid2>

                      <Grid2 container spacing={1}>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                          {renderMeta(
                            "Recipient",
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {getRecipientName(coupon)}
                              <Stack
                                direction="row"
                                spacing={0.25}
                                alignItems="center"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <CustomTypography
                                  variant="body2"
                                  color={colors.subtitle}
                                  fontWeight={500}
                                  sx={{ display: "block" }}
                                >
                                  {typeof email === "string"
                                    ? email.toLowerCase()
                                    : email}
                                </CustomTypography>
                                {email &&
                                  email !== "-" &&
                                  renderCopyButton(email, "Email")}
                              </Stack>
                            </Box>,
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 6, md: 2 }}>
                          {renderMeta(
                            "Customer",
                            truncateText(coupon.customer_name, 36),
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 6, md: 2 }}>
                          {renderMeta(
                            "Template",
                            truncateText(coupon.template_name, 36),
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 6, md: 2 }}>
                          {renderMeta(
                            "Selected Pack",
                            truncateText(
                              coupon.pack_names || coupon.variants_in_str,
                              40,
                            ),
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                          {renderMeta(
                            "Reminders",
                            coupon.reminders_sent ?? "-",
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                          {renderMeta("Sent", formatDate(coupon.sent_date))}
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                          {renderMeta(
                            "Opened",
                            formatDate(coupon.opened_date),
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                          {renderMeta(
                            "Redeemed",
                            formatDate(coupon.redeemed_date),
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                          {renderMeta(
                            "Reactivated",
                            formatDate(coupon.reactivation_date),
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 8, md: 4 }}>
                          {renderMeta(
                            "Last Reminder",
                            formatDate(coupon.last_reminder_sent_date),
                          )}
                        </Grid2>
                      </Grid2>
                    </Box>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    px: { xs: 1, sm: 1.25 },
                    py: 0.6,
                    borderTop: `1px solid ${colors.borderSoft}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                    alignItems="center"
                  >
                    <CustomTypography
                      variant="body3"
                      color={colors.subtitle}
                      fontWeight={600}
                    >
                      Created on {formatDate(coupon.created_date, false, true)}
                    </CustomTypography>
                    <CustomTypography
                      variant="body3"
                      color={colors.subtitle}
                      fontWeight={600}
                    >
                      |
                    </CustomTypography>
                    <CustomTypography
                      variant="body3"
                      color={colors.subtitle}
                      fontWeight={600}
                    >
                      Updated on {formatDate(coupon.modified_date)}
                    </CustomTypography>
                  </Stack>

                  <AppButton
                    label="View"
                    variant="secondary"
                    onClick={() => onViewDetails(coupon)}
                    customStyles={actionButtonStyles}
                  />
                </Box>
              </Paper>
            );
          })}
        </Stack>
      </Box>

      {coupons.length === 0 && (
        <Box
          sx={{
            ...modernEmptyStateSx(theme),
            p: 4,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: colors.surface,
          }}
        >
          <CustomTypography
            variant="heading6"
            fontWeight={600}
            color={colors.subtitle}
          >
            No coupons match the current filters.
          </CustomTypography>
        </Box>
      )}

      {total_count > 0 && (
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: colors.surface,
          }}
        >
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
            rowsPerPageOptions={[30, 50, 100, 200, 500]}
          />
        </Box>
      )}
    </Paper>
  );
};

export default CouponsTable;
